import axios, { AxiosError, AxiosResponse } from "axios";

export const API_ERROR_EVENT = "api-error";

const STATUS_LABELS: Record<number, string> = {
  400: "Bad request",
  401: "Unauthorized",
  403: "Unauthorized/forbidden access",
  404: "Not found",
  408: "Request timeout",
  409: "Conflict",
  422: "Unprocessable request",
  429: "Too many requests",
  500: "Internal server error",
  502: "Network/gateway error",
  503: "Service unavailable",
  504: "Gateway timeout",
};

type ApiErrorBody = {
  msg?: unknown;
  message?: unknown;
  error?: unknown;
  originalMsg?: unknown;
};

export type NormalizedApiError = {
  status: number;
  rawMessage: string;
  displayMessage: string;
  isSessionExpired: boolean;
};

let lastNotifiedAt = 0;

export const markApiErrorNotified = () => {
  lastNotifiedAt = Date.now();
};

export const wasApiErrorJustNotified = () => Date.now() - lastNotifiedAt < 800;

const readMessageField = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

export const readApiMessage = (data: unknown): string => {
  if (typeof data === "string") return data.trim();
  if (!data || typeof data !== "object") return "";

  const body = data as ApiErrorBody;
  return (
    readMessageField(body.originalMsg) ||
    readMessageField(body.msg) ||
    readMessageField(body.message) ||
    readMessageField(body.error)
  );
};

export const statusLabel = (status: number): string =>
  STATUS_LABELS[status] || "Request failed";

export const formatApiErrorMessage = (
  status: number,
  apiMessage?: string,
): string => {
  const label = statusLabel(status);
  const cleaned = apiMessage?.trim() ?? "";
  if (!cleaned || cleaned.toLowerCase() === label.toLowerCase()) {
    return `${status} ${label}`;
  }
  return `${status} ${label}: ${cleaned}`;
};

export const isJwtOrSessionExpired = (status: number): boolean =>
  status === 401 || status === 403;

const networkStatus = (error: AxiosError): number => {
  if (
    error.code === "ERR_NETWORK" ||
    error.code === "ECONNABORTED" ||
    error.code === "ETIMEDOUT"
  ) {
    return 502;
  }
  return 500;
};

export const normalizeApiError = (error: AxiosError): NormalizedApiError => {
  const status = error.response?.status ?? networkStatus(error);
  const rawMessage = readApiMessage(error.response?.data);
  return {
    status,
    rawMessage,
    displayMessage: formatApiErrorMessage(status, rawMessage),
    isSessionExpired: isJwtOrSessionExpired(status),
  };
};

export const attachDisplayMessage = (
  error: AxiosError,
  normalized: NormalizedApiError,
) => {
  (error as AxiosError & { apiErrorMessage?: string }).apiErrorMessage =
    normalized.displayMessage;

  if (!error.response) {
    error.response = {
      data: {
        msg: normalized.displayMessage,
        originalMsg: normalized.rawMessage,
      },
      status: normalized.status,
      statusText: statusLabel(normalized.status),
      headers: {},
      config: error.config!,
    } as AxiosResponse;
    return;
  }

  const data = error.response.data;
  if (data && typeof data === "object") {
    const body = data as ApiErrorBody;
    if (body.originalMsg === undefined) {
      body.originalMsg = normalized.rawMessage;
    }
    body.msg = normalized.displayMessage;
    return;
  }

  error.response.data = {
    msg: normalized.displayMessage,
    originalMsg:
      normalized.rawMessage || (typeof data === "string" ? data : ""),
  };
};

export const notifyApiError = (message: string) => {
  markApiErrorNotified();
  window.dispatchEvent(
    new CustomEvent(API_ERROR_EVENT, { detail: { message } }),
  );
};

export const getRawApiMessage = (error: unknown): string => {
  if (!axios.isAxiosError(error)) return "";
  return readApiMessage(error.response?.data);
};

export const getApiErrorDisplay = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const tagged = error as AxiosError & { apiErrorMessage?: string };
    if (tagged.apiErrorMessage) return tagged.apiErrorMessage;
    return normalizeApiError(error).displayMessage;
  }

  if (error instanceof Error && error.message) return error.message;
  return "500 Internal server error";
};
